import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import InventoryItemTile from '@/components/store/InventoryItemTile'
import {
  HOME_SECTION_ROWS,
  STOREFRONT_GRID_CLASS,
  useStorefrontGridColumnCount,
} from '@/hooks/useStorefrontGridColumnCount'
import { useStorefrontCategories } from '@/hooks/useStorefrontCategories'
import type { InventoryItemNameRow, StorefrontCategory } from '@/lib/api'
import { itemAllowsCart } from '@/lib/inventoryItemDisplay'

type Props = {
  items: InventoryItemNameRow[]
  isFavorite: (id: string) => boolean
  onToggleFavorite: (id: string) => void
}

type Group = { id: string; name: string; items: InventoryItemNameRow[] }

/** Catch-all category: most POS rows land here, so it goes last or it would bury every other category. */
const CATCH_ALL_SLUG = 'general-assorted'

function byName(a: InventoryItemNameRow, b: InventoryItemNameRow): number {
  return (a.item_name || '').localeCompare(b.item_name || '', undefined, { sensitivity: 'base' })
}

/**
 * Home feed: one section per category with in-stock items, each previewing up to four rows;
 * the heading and "See all" open the full category on `/items`.
 */
export default function CategoryItemsFeed({ items, isFavorite, onToggleFavorite }: Props) {
  const { categories } = useStorefrontCategories(items)
  const gridCols = useStorefrontGridColumnCount('items')

  const groups = useMemo<Group[]>(() => {
    const byCat = new Map<string, InventoryItemNameRow[]>()
    for (const row of items) {
      if (!row.category_id || !itemAllowsCart(row.in_stock)) continue
      const list = byCat.get(row.category_id)
      if (list) list.push(row)
      else byCat.set(row.category_id, [row])
    }
    const rank = (c: StorefrontCategory) =>
      c.slug === CATCH_ALL_SLUG ? Number.MAX_SAFE_INTEGER : (c.display_order ?? Number.MAX_SAFE_INTEGER - 1)
    return [...categories]
      .sort((a, b) => rank(a) - rank(b))
      .flatMap((c) => {
        const list = byCat.get(c.category_id)
        return list?.length ? [{ id: c.category_id, name: c.name, items: [...list].sort(byName) }] : []
      })
  }, [items, categories])

  if (groups.length === 0) return null

  const previewCount = HOME_SECTION_ROWS * gridCols

  return (
    <div className="mt-8 space-y-8 sm:mt-10 sm:space-y-10" aria-label="Shop by category">
      {groups.map((g) => (
        <section key={g.id} aria-labelledby={`cat-${g.id}`}>
          <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
            <h2 id={`cat-${g.id}`} className="font-display text-lg font-bold text-gray-800 sm:text-xl lg:text-2xl">
              <Link to={`/items?category=${g.id}`} className="hover:text-primary-700">
                {g.name}
              </Link>
            </h2>
            <Link
              to={`/items?category=${g.id}`}
              className="inline-flex shrink-0 items-center gap-0.5 text-sm font-semibold text-primary-700 hover:text-primary-800"
            >
              See all {g.items.length}
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
          <div className={STOREFRONT_GRID_CLASS}>
            {g.items.slice(0, previewCount).map((item) => (
              <InventoryItemTile
                key={item.inventory_id}
                item={item}
                isFavorite={isFavorite(item.inventory_id)}
                onToggleFavorite={() => onToggleFavorite(item.inventory_id)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
